// PRD.310 — Visit Note Medical History: the pure, DOM-free pieces.
//
// Kept out of the React component so the persistence + dictation rules are
// unit-testable under the repo's node:test harness. No React, no PowerSync, no
// `import.meta.env`.

import { appendTranscript } from '../../../shell/elia-ai/elia-voice-dictation';

/**
 * The canonical sub-module key. Medical History is a GENERIC Visit Note
 * section — Dental only switches it on by default (see
 * `GUIDED_PRESET_DEFAULT_ON` in data-access), it does not own it.
 */
export const MEDICAL_HISTORY_KEY = 'medical_history';

/**
 * Normalize the editor value for persistence into
 * `patient_encounters.medical_history`.
 *
 * Mirrors `chief_complaint` (PRD.213): an empty / whitespace-only editor stores
 * SQL NULL rather than an empty string, so "never filled in" and "cleared"
 * look the same to every reader.
 */
export function toStoredMedicalHistory(text: string): string | null {
  return text.trim() ? text : null;
}

/** The editor value for an encounter row's stored column. */
export function fromStoredMedicalHistory(stored: string | null | undefined): string {
  return stored ?? '';
}

/**
 * True when `next` differs from what was last written, i.e. an autosave is
 * warranted. Compared on the STORED form so a trailing-whitespace-only edit of
 * an empty field doesn't churn writes.
 */
export function needsSave(next: string, lastSaved: string): boolean {
  return toStoredMedicalHistory(next) !== toStoredMedicalHistory(lastSaved);
}

/**
 * Insert a finished transcript into the Medical History editor.
 *
 * Delegates to the established Elia dictation rule (PRD.279 `appendTranscript`):
 * an empty editor becomes the transcript; a populated editor keeps its text and
 * gains the transcript after a blank line; an empty transcript changes nothing.
 * Voice therefore NEVER silently overwrites typed clinical text — it is only a
 * second input method for the same field.
 */
export function insertMedicalHistoryTranscript(current: string, transcript: string): string {
  return appendTranscript(current, transcript);
}

export { appendTranscript };
